import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { Controller } from "../interface/controller.interface";
import { HTTPMethod } from "../model/http-method.enum";
import { HTTPRequestBuilder } from "../model/http.request.model";
import { HTTPService } from "./http.service";
import { orElse } from "../model/operator/or-else.operator";
import { ResellerPaymentEntity } from "../persistence/dto/reseller-payment-entity.model";
import { PagedQuery } from "../persistence/dto/paged-query.model";
import { AccountMovement } from "../persistence/view-dto/account-movement.model";
import { MimeType } from "../model/mime-type.enum";
import { PagedQueryResult } from "../persistence/dto/paged-query-result.model";
import { ResellerPayment } from "../persistence/view-dto/reseller-payment.model";
import { DownloadResellerPayment } from "../persistence/view-dto/download-reseller-payment.model";
import { AccountStament } from "../persistence/view-dto/account-stament.model";
import { DelinquencyQuery } from "../persistence/dto/delinquency-query.model";
import { AsyncJobId } from "../persistence/dto/async-job-id.model";

	/**
	 * @description
	 *  Servicio reflejo del controlador de transacciones de revendedores
	 *  (deudas, movimientos, pagos, etc.), del backend.
	 *
	 * @see gigot-backend/Controllers/Users/ResellerStatementController.cs
	 */

@Injectable({ providedIn: "root" })
export class ResellerStatementService implements Controller {

    constructor(private httpService: HTTPService) { }

    public allResellerPaymentEntities(): Observable<ResellerPaymentEntity[]> {
        return this.httpService.send<typeof undefined, ResellerPaymentEntity[]>(
            new HTTPRequestBuilder()
                .method(HTTPMethod.GET)
                .controllerPath(this.basePath())
                .relativePath("PaymentEntities")
                .build())
            .pipe(
                orElse<ResellerPaymentEntity[]>([] as ResellerPaymentEntity[]));
    }

    public basePath(): string {
        return "api/resellerStatement";
    }

    public getResellerStamentsBalance<T>(pagedQuery: PagedQuery<T>): Observable<PagedQueryResult<AccountStament>> {
        console.log(pagedQuery)
        return this.httpService.send<PagedQuery<T>, PagedQueryResult<AccountStament>>(
            new HTTPRequestBuilder<PagedQuery<T>>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath("getResellerStamentsBalance")
                .payload(pagedQuery)
                .build());
    }

    public delinquencyReportByCampaign(delinquencyQuery: DelinquencyQuery): Observable<AsyncJobId> {
        return this.httpService.send<DelinquencyQuery, AsyncJobId>(
            new HTTPRequestBuilder<DelinquencyQuery>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath("delinquencyReportByCampaign")
                .payload(delinquencyQuery)
                .build())
			.pipe(map((response) => AsyncJobId.from(response)));
    }

    public downloadStaments<T>(pagedQuery: PagedQuery<T>): Observable<Blob> {
        return this.httpService.send<PagedQuery<T>, Blob>(
            new HTTPRequestBuilder<PagedQuery<T>>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath("downloadStaments")
                .payload(pagedQuery)
                .responseType("blob")
                .build())
            .pipe(
                map((response) => new Blob([response], { type: MimeType.MICROSOFT_EXCEL_XLSX })));
    }

    public delinquencyReportByReseller(delinquencyQuery: DelinquencyQuery): Observable<AsyncJobId> {
        return this.httpService.send<DelinquencyQuery, AsyncJobId>(
            new HTTPRequestBuilder<DelinquencyQuery>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath("delinquencyReportByReseller")
                .payload(delinquencyQuery)
                .build())
			.pipe(map((response) => AsyncJobId.from(response)));
    }

    public downloadMovements<T>(pagedQuery: PagedQuery<T>): Observable<Blob> {
        return this.httpService.send<PagedQuery<T>, Blob>(
            new HTTPRequestBuilder<PagedQuery<T>>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath("OrdersTransaction/downloadMovements")
                .payload(pagedQuery)
                .responseType("blob")
                .build())
            .pipe(
                map((response) => new Blob([response], { type: MimeType.MICROSOFT_EXCEL_XLSX })));
    }

    public downloadResellerPaymentJobs(filter: DownloadResellerPayment): Observable<Blob> {
        return this.httpService.send<DownloadResellerPayment, Blob>(
            new HTTPRequestBuilder<DownloadResellerPayment>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath("downloadResellerPaymentJobs")
                .payload(filter)
                .responseType("blob")
                .build())
            .pipe(
                map((response) => new Blob([response], { type: MimeType.MICROSOFT_EXCEL_XLSX })));
    }

    public findAccountMovements<T>(pagedQuery: PagedQuery<T>): Observable<PagedQueryResult<AccountMovement>> {
        return this.httpService.send<PagedQuery<T>, PagedQueryResult<AccountMovement>>(
            new HTTPRequestBuilder<PagedQuery<T>>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath("OrdersTransaction/findTransacionMovements")
                .payload(pagedQuery)
                .build());
    }

    public findFilesMovements<T>(pagedQuery: PagedQuery<T>): Observable<PagedQueryResult<ResellerPayment>> {
        return this.httpService.send<PagedQuery<T>, PagedQueryResult<ResellerPayment>>(
            new HTTPRequestBuilder<PagedQuery<T>>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath("resellerPaymentJobs")
                .payload(pagedQuery)
                .build());
    }
}
